'use client';

import { createAuthClient } from 'better-auth/react';
import { adminClient, organizationClient } from 'better-auth/client/plugins';
import { useCallback } from 'react';
import { BACKEND_URL } from '@/lib/constants';

export const authClient = createAuthClient({
  baseURL: `${BACKEND_URL}/auth`,
  plugins: [organizationClient(), adminClient()],
  fetchOptions: { credentials: 'include' },
});

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  image?: string | null;
}

export function useAuth(): {
  user: AuthUser | null;
  organizationId: string | null;
  loading: boolean;
} {
  const { data, isPending } = authClient.useSession();
  return {
    user: data?.user ?? null,
    organizationId:
      (data?.session as { activeOrganizationId?: string | null } | undefined)
        ?.activeOrganizationId ?? null,
    loading: isPending,
  };
}

function jwtExpiresAt(token: string): number {
  try {
    const segment = token.split('.')[1];
    const payload = JSON.parse(
      atob(segment.replace(/-/g, '+').replace(/_/g, '/'))
    ) as { exp?: number };
    return (payload.exp ?? 0) * 1000;
  } catch {
    return 0;
  }
}

// The JWT is short-lived; cache it module-wide and re-mint through a single
// in-flight request when it is within a minute of expiry.
let cachedJwt: { token: string; expiresAt: number } | null = null;
let inflightJwt: Promise<string | undefined> | null = null;

async function mintJwt(): Promise<string | undefined> {
  const { data } = await authClient.$fetch<{ token: string }>('/token');
  if (!data?.token) return undefined;
  cachedJwt = { token: data.token, expiresAt: jwtExpiresAt(data.token) };
  return data.token;
}

export async function fetchAccessToken(): Promise<string | undefined> {
  if (cachedJwt && cachedJwt.expiresAt - Date.now() > 60_000) {
    return cachedJwt.token;
  }
  inflightJwt ??= mintJwt().finally(() => {
    inflightJwt = null;
  });
  return inflightJwt;
}

export function useAccessToken(): {
  getAccessToken: () => Promise<string | undefined>;
  loading: boolean;
} {
  const { isPending } = authClient.useSession();
  const getAccessToken = useCallback(fetchAccessToken, []);
  return { getAccessToken, loading: isPending };
}

/** Drop the cached backend JWT. Call after anything that changes the active
 *  organization (claiming an onboarding link, accepting an invitation) so the
 *  next API call carries a token with the new org_id claim. */
export function clearAccessTokenCache(): void {
  cachedJwt = null;
}

export async function signOutEverywhere(): Promise<void> {
  cachedJwt = null;
  await authClient.signOut();
  window.location.href = '/sign-in';
}
